import type { overview as en } from '@/i18n/locales/en/overview';
import type { Translation } from '@/i18n/types';

export const overview: Translation<typeof en> = {
  page: {
    title: 'Visão geral',
    lead: 'Como está {{company}}?',
    noScope: 'Selecione uma empresa para ver a visão geral.',
    noDepartments: 'Nenhum departamento aqui tem estatísticas que possa ver.',
    disclaimer:
      'Todos os valores são indicações de bem-estar ao nível do grupo e não se destinam a ser um diagnóstico médico.',
  },
  summary: {
    caption: 'Resumo de IA com base em dados anonimizados e agregados.',
  },
  figures: {
    vitality: 'Vitalidade',
    stress: 'Stress',
    sleep: 'Sono',
    engagement: 'Envolvimento',
    activeParticipants: 'Participantes ativos',
  },
  figuresInfo: {
    vitality:
      'Uma pontuação composta com base em sono, atividade, HRV, stress e frequência cardíaca.',
    stress: 'Nível médio de stress, medido diretamente pelo wearable.',
    sleep: 'Duração média do sono por noite, medida diretamente pelo wearable.',
    engagement: 'Proporção de colaboradores que usam ativamente a aplicação.',
    activeParticipants: 'Número de colaboradores ativos no último período.',
  },
  legend: {
    title: 'O que está a ver',
    measured:
      'Medido diretamente — diretamente do wearable NOLATR ou de um sensor ligado. Maior fiabilidade.',
    aiDerived:
      'Derivado por IA — não é uma medição direta. A IA combina vários sinais num indicador.',
    usage: 'Uso & comportamento — interações reais na aplicação, não uma medição de saúde.',
  },
  developments: {
    title: 'Principais desenvolvimentos',
    empty: 'Sem desenvolvimentos relevantes neste período.',
    stress: {
      title: 'Oportunidade de oferecer descanso (stress elevado)',
      body: 'Um momento de descanso simpático ou uma dica de respiração pode ajudar a equipa a recarregar energias.',
    },
    sleep: {
      title: 'Oportunidade: sono abaixo do objetivo',
      body: 'Uma dica de recuperação direcionada e positiva pode apoiar bem a equipa.',
    },
    vitalityGood: {
      title: 'A vitalidade está a evoluir bem',
      body: 'A equipa está a manter bem o seu ritmo atual — um bom momento para reconhecer isso.',
    },
    needsReactivation: {
      title: '{{count}} pessoas precisam de reativação',
      body: 'Uma mensagem de reativação curta e pessoal costuma funcionar melhor.',
    },
    mostActiveWeekday: {
      title: 'Mais energia: {{weekday}}',
      body: 'Planeie desafios e comunicações em torno do dia mais ativo para um alcance máximo.',
    },
    challengeParticipation: {
      title: 'Participação no desafio {{pct}}%',
      body: 'Um novo desafio de equipa acessível pode envolver mais pessoas.',
    },
  },
  actions: {
    title: 'Ações para RH',
    empty: 'Sem ações recomendadas neste momento.',
    sendReminder: {
      title: '{{count}} colaboradores ainda não ativados',
      subtitle: 'Envie um lembrete',
    },
    reactivate: {
      title: '{{count}} colaboradores precisam de reativação',
      subtitle: 'Entre em contacto',
    },
    stressCheckIn: {
      title: 'Stress elevado na equipa',
      subtitle: 'Considere uma breve conversa sobre a carga de trabalho',
    },
    sleepReminder: {
      title: 'Sono abaixo do objetivo na equipa',
      subtitle: 'Considere um lembrete sobre rotinas noturnas e tempo de ecrã',
    },
    activeChallenge: {
      title: "'{{name}}' está em curso — planeie o próximo",
      subtitle: 'Desafios',
    },
    pilotEvaluation: {
      interim: {
        title: "O piloto '{{name}}' está pronto para a avaliação intermédia",
        subtitle: 'Ver piloto',
      },
      final: {
        title: "O piloto '{{name}}' está pronto para a avaliação final",
        subtitle: 'Ver piloto',
      },
    },
  },
  energy: {
    title: 'Energia e bem-estar no trabalho',
    subtitle:
      'Como a equipa vive o dia de trabalho — energia, pausas, ritmo de trabalho e envolvimento. Apenas agregado, nunca individual.',
    badge: 'Agregado e anonimizado',
    tbd: 'A determinar',
    missingData: {
      noSource: 'Índice de energia e eNPS: fonte de dados ainda por determinar.',
      insufficientParticipants: 'Participantes insuficientes para {{metrics}}.',
    },
    privacyNote:
      'Informações de apoio, não monitorização. Bem-estar indicativo ao nível do grupo — sem interpretação médica.',
    metrics: {
      energyIndex: 'Índice de energia',
      breakAdoption: 'Adoção de pausas',
      focusRhythm: 'Ritmo de foco',
      coachAdoption: 'Adoção do coach',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Estado dos pilotos',
    empty: 'Ainda sem pilotos.',
    participantsAndDay: '{{count}} participantes · dia {{day}} de {{total}}',
    viewReport: 'Ver relatório do piloto',
  },
  challenges: {
    title: 'Desafios ativos',
    empty: 'Ainda sem desafios.',
    active: 'Ativos',
    total: 'Total',
    participants: 'Participantes',
    activeBadge: '{{count}} ativos',
    manageLink: 'Gerir desafios',
    noneActive: 'Nenhum desafio ativo neste momento.',
    participantsCount: '{{count}} participantes',
  },
  trends: {
    title: 'Tendências',
    filters: {
      allDepartments: 'Todos os departamentos',
      participants: {
        label: 'Participantes',
        all: 'Todos os participantes',
        manager: 'Gestores',
        employee: 'Colaboradores',
      },
    },
    vitalityScore: 'Pontuação de vitalidade ao longo do tempo',
    activeUsersPerWeek: 'Utilizadores ativos por semana',
    sleep: 'Tendência do sono',
    stress: 'Tendência do stress',
    hrv: 'Tendência de VFC',
    recovery: 'Tendência de recuperação',
    activity: 'Tendência de atividade',
    notAvailable: 'Ainda sem fonte de dados para esta tendência — não disponível nesta versão.',
    vsPreviousPeriod: 'vs. período anterior',
    info: {
      vitalityScore:
        'Uma pontuação de vitalidade composta por dia, com base em todos os sinais de vitalidade.',
      activeUsersPerWeek:
        'Número de colaboradores únicos que usaram ativamente a aplicação a cada semana.',
      sleep: 'Duração média do sono por dia, medida diretamente pelo wearable.',
      stress: 'Nível médio de stress por dia, medido diretamente pelo wearable.',
      hrv: 'Variabilidade média da frequência cardíaca (HRV) por dia, um indicador de recuperação.',
      recovery: 'Uma pontuação de recuperação composta por dia, com base em sono, HRV e atividade.',
      activity: 'Número médio de passos por dia, medido diretamente pelo wearable.',
    },
  },
};
